// src/utils/deviceName.js

const pick = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

/**
 * The device's NAME only — the user's alias, then the provider's — or null.
 * Screens that show the vendor id on a second line use this to know whether
 * the heading is a name or already the id (so the id is never shown twice).
 */
export function deviceNameOnly(device) {
  return pick(device?.alias) || pick(device?.meta?.alias) || null;
}

/**
 * The name a device should be shown under, everywhere in the admin dashboard.
 *
 * ⚠️ Top-level `alias` is NOT enough. A Tapo device is inserted by sync with
 * `alias: null` — the name TP-Link reports lives only in `meta.alias` until the
 * user renames the device in the app. Screens that read `alias` alone showed
 * the raw vendor id for every unrenamed device, while Overview, History and
 * the Dashboard (which read `meta.alias`) showed its real name.
 *
 * Order matches what the backend itself uses for history (`deviceAlias`) and
 * provider device lists: the user's own name, then the provider's name.
 *
 * @param {object|null} device   a Device row (from the devices endpoint /
 *                               deviceIndex)
 * @param {string|null} fallback shown only when there is no name AND no
 *                               vendor id — e.g. the vendor id from a schedule
 *                               action when the device row is missing
 */
export function deviceDisplayName(device, fallback = null) {
  return (
    deviceNameOnly(device) ||
    // String-checked: a non-string id would crash the JSX that renders it.
    pick(device?.deviceId) ||
    fallback ||
    null
  );
}

export default deviceDisplayName;
